<template>
  <AnalyticsWidget
    title="Quote Breakdown"
    subTitle="Quotes created/converted during the date ranges"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">

    <LoadingPanel :loading="loading" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th></th>
          <th>Open Quotes</th>
          <th>Quote Count</th>
          <th>Converted</th>
          <th>Converted Count</th>
          <th>Cancelled</th>
          <th>Cancelled Count</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr class="highlight">
          <td>
            Today
            <InlineHelpPopover title="Today" placement="right" inline>
              <p>Today&apos;s quotes compared to previous working day&apos;s quotes on {{formatDate(quoteBreakdownData.day.previous.startDate)}}</p>              
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.day.current.quoteValue" :previous="quoteBreakdownData.day.previous.quoteValue" highlight>{{formatCurrency(quoteBreakdownData.day.current.quoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.day.current.quoteCount" :previous="quoteBreakdownData.day.previous.quoteCount">{{quoteBreakdownData.day.current.quoteCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.day.current.convertedQuoteValue" :previous="quoteBreakdownData.day.previous.convertedQuoteValue" highlight>{{formatCurrency(quoteBreakdownData.day.current.convertedQuoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.day.current.convertedQuoteCount" :previous="quoteBreakdownData.day.previous.convertedQuoteCount">{{quoteBreakdownData.day.current.convertedQuoteCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.day.current.cancelledQuoteValue" :previous="quoteBreakdownData.day.previous.cancelledQuoteValue" highlight>{{formatCurrency(quoteBreakdownData.day.current.cancelledQuoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.day.current.cancelledQuoteCount" :previous="quoteBreakdownData.day.previous.cancelledQuoteCount">{{quoteBreakdownData.day.current.cancelledQuoteCount}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Week
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This week&apos;s quotes compared to same day of the previous week&apos;s quotes from {{formatDate(quoteBreakdownData.week.previous.startDate)}} to {{formatDate(quoteBreakdownData.week.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.week.current.quoteValue" :previous="quoteBreakdownData.week.previous.quoteValue" highlight>{{formatCurrency(quoteBreakdownData.week.current.quoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.week.current.quoteCount" :previous="quoteBreakdownData.week.previous.quoteCount">{{quoteBreakdownData.week.current.quoteCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.week.current.convertedQuoteValue" :previous="quoteBreakdownData.week.previous.convertedQuoteValue" highlight>{{formatCurrency(quoteBreakdownData.week.current.convertedQuoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.week.current.convertedQuoteCount" :previous="quoteBreakdownData.week.previous.convertedQuoteCount">{{quoteBreakdownData.week.current.convertedQuoteCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.week.current.cancelledQuoteValue" :previous="quoteBreakdownData.week.previous.cancelledQuoteValue" highlight>{{formatCurrency(quoteBreakdownData.week.current.cancelledQuoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.week.current.cancelledQuoteCount" :previous="quoteBreakdownData.week.previous.cancelledQuoteCount">{{quoteBreakdownData.week.current.cancelledQuoteCount}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Month
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This month&apos;s quotes compared to same day of the previous month&apos;s quotes from {{formatDate(quoteBreakdownData.month.previous.startDate)}} to {{formatDate(quoteBreakdownData.month.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.month.current.quoteValue" :previous="quoteBreakdownData.month.previous.quoteValue" highlight>{{formatCurrency(quoteBreakdownData.month.current.quoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.month.current.quoteCount" :previous="quoteBreakdownData.month.previous.quoteCount">{{quoteBreakdownData.month.current.quoteCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.month.current.convertedQuoteValue" :previous="quoteBreakdownData.month.previous.convertedQuoteValue" highlight>{{formatCurrency(quoteBreakdownData.month.current.convertedQuoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.month.current.convertedQuoteCount" :previous="quoteBreakdownData.month.previous.convertedQuoteCount">{{quoteBreakdownData.month.current.convertedQuoteCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.month.current.cancelledQuoteValue" :previous="quoteBreakdownData.month.previous.cancelledQuoteValue" highlight>{{formatCurrency(quoteBreakdownData.month.current.cancelledQuoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.month.current.cancelledQuoteCount" :previous="quoteBreakdownData.month.previous.cancelledQuoteCount">{{quoteBreakdownData.month.current.cancelledQuoteCount}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This FY
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This financial year&apos;s quotes compared to same day of the previous financial year&apos;s quotes from {{formatDate(quoteBreakdownData.financialYear.previous.startDate)}} to {{formatDate(quoteBreakdownData.financialYear.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.financialYear.current.quoteValue" :previous="quoteBreakdownData.financialYear.previous.quoteValue" highlight>{{formatCurrency(quoteBreakdownData.financialYear.current.quoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.financialYear.current.quoteCount" :previous="quoteBreakdownData.financialYear.previous.quoteCount">{{quoteBreakdownData.financialYear.current.quoteCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.financialYear.current.convertedQuoteValue" :previous="quoteBreakdownData.financialYear.previous.convertedQuoteValue" highlight>{{formatCurrency(quoteBreakdownData.financialYear.current.convertedQuoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.financialYear.current.convertedQuoteCount" :previous="quoteBreakdownData.financialYear.previous.convertedQuoteCount">{{quoteBreakdownData.financialYear.current.convertedQuoteCount}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.financialYear.current.cancelledQuoteValue" :previous="quoteBreakdownData.financialYear.previous.cancelledQuoteValue" highlight>{{formatCurrency(quoteBreakdownData.financialYear.current.cancelledQuoteValue)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="quoteBreakdownData.financialYear.current.cancelledQuoteCount" :previous="quoteBreakdownData.financialYear.previous.cancelledQuoteCount">{{quoteBreakdownData.financialYear.current.cancelledQuoteCount}}</AnalyticsBreakdownValueCell>
        </tr>
      </template>

    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

export default {
  name: 'QuoteBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell
  },
  props: {
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    },
  },
  data() {
    return {
      loading: true,
      quoteBreakdownData: {}
    }
  },
  watch: {
    contactId: function() {
      this.requestData()
    },
    filter: function() {
      this.requestData()
    }
  },
  mounted() {
    this.loading = true
    this.requestData()

    this.subscribeEndpoint('/orders', () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    requestData() {
      var params = {
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      this.$apiService.analyticsService.getQuoteValueBreakdown(params)
      .then(response => {
        this.quoteBreakdownData = response.data
      })
      .finally(() => {
        this.loading = false
      })
      .catch(err => {
      })
    }
  }
}
</script>